import {
  CARRIER_NAME_MAP,
  CarrierKey,
  CarrierKeyForName,
  CarrierName,
} from "../pages/BBE/constants";
import {
  CarrierBonusYear,
  GuardianBonusYearSettings,
  LincolnBonusYearSettings,
  PersistencyPayout,
  PrincipalBonusYearSettings,
} from "../pages/BBE/types";
import { formatCurrencyWithRound } from "../utils";

import { useCurrentBonusYearSettingsQuery } from "./useBbe";

const isPrincipalSettings = (
  _settings: PrincipalBonusYearSettings,
  type: CarrierKey,
): _settings is PrincipalBonusYearSettings => type === "principal";

const isLincolnSettings = (
  _settings: CarrierBonusYear,
  type: CarrierKey,
): _settings is LincolnBonusYearSettings => type === "lincoln";

const isGuardianSettings = (
  _settings: CarrierBonusYear,
  type: CarrierKey,
): _settings is GuardianBonusYearSettings => type === "guardian";

const formatPercent = (value?: number | null): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const percent = value * 100;
  const roundedPercent = Number(percent.toFixed(2));
  const display = Number.isInteger(roundedPercent)
    ? roundedPercent.toFixed(0)
    : roundedPercent.toFixed(2);
  return `${display}%`;
};

const formatPayouts = (values: (number | null | undefined)[]): string => {
  const formatted = values
    .map((value) => formatPercent(value))
    .filter((value): value is string => Boolean(value));

  return formatted.length ? formatted.join(" / ") : "—";
};

const getLincolnNbLevels = (
  settings?: LincolnBonusYearSettings,
): { payout: number; threshold?: number }[] => {
  const payouts = [
    settings?.newBonusLevelOnePct,
    settings?.newBonusLevelTwoPct,
    settings?.newBonusLevelThreePct,
    settings?.newBonusLevelFourPct,
  ];

  return payouts.flatMap((payout, index) =>
    typeof payout === "number" && payout !== 0
      ? [{ payout, threshold: settings?.nbThresholds?.[`NB ${index + 1}`] }]
      : [],
  );
};

const getBusinessThresholds = (
  carrierType: CarrierKey,
  bonusYearData: CarrierBonusYear,
  startOfYearPremiumInput?: number,
): string | null => {
  let newBusinessThresholds: string | null = null;

  if (isGuardianSettings(bonusYearData, carrierType)) {
    const startOfYearPremium = startOfYearPremiumInput ?? 0;

    if (!startOfYearPremium || Number.isNaN(startOfYearPremium)) {
      return null;
    }

    newBusinessThresholds = Object.values(bonusYearData.nbThresholds ?? {})
      .sort((a, b) => b - a) // sort in descending order
      .map((pct) => formatCurrencyWithRound(startOfYearPremium * pct))
      .join(" / ");
  } else if (isPrincipalSettings(bonusYearData, carrierType)) {
    newBusinessThresholds = bonusYearData?.nbThreshold;
  } else if (isLincolnSettings(bonusYearData, carrierType)) {
    const thresholds = getLincolnNbLevels(bonusYearData)
      .map((level) => level.threshold)
      .filter(
        (threshold): threshold is number => typeof threshold === "number",
      );

    newBusinessThresholds = thresholds.length
      ? thresholds
          .map((threshold) => formatCurrencyWithRound(threshold))
          .join(" / ")
      : null;
  }

  return newBusinessThresholds;
};

const getPersistencyPayouts = (
  carrierType: CarrierKey,
  bonusYearData?: CarrierBonusYear,
):
  | string
  | {
      persistencyPayoutsA: PersistencyPayout;
      persistencyPayoutsB: PersistencyPayout;
    }
  | null => {
  let persistencyPayouts:
    | string
    | {
        persistencyPayoutsA: PersistencyPayout;
        persistencyPayoutsB: PersistencyPayout;
      } = null;

  if (isGuardianSettings(bonusYearData, carrierType)) {
    persistencyPayouts = formatPayouts([
      bonusYearData?.persistencyBonusLevelOnePct,
      bonusYearData?.persistencyBonusLevelTwoPct,
      bonusYearData?.persistencyBonusLevelThreePct,
    ]);
  } else if (isPrincipalSettings(bonusYearData, carrierType)) {
    persistencyPayouts = bonusYearData?.persistencyPayouts;
  } else if (isLincolnSettings(bonusYearData, carrierType)) {
    persistencyPayouts = {
      persistencyPayoutsA: {
        value: formatPayouts([
          bonusYearData?.persistencyBonusLevelOnePctA,
          bonusYearData?.persistencyBonusLevelTwoPctA,
          bonusYearData?.persistencyBonusLevelThreePctA,
          bonusYearData?.persistencyBonusLevelFourPctA,
        ]),
        label: "Persistency Payouts (A)",
      },
      persistencyPayoutsB: {
        value: formatPayouts([
          bonusYearData?.persistencyBonusLevelOnePctB,
          bonusYearData?.persistencyBonusLevelTwoPctB,
          bonusYearData?.persistencyBonusLevelThreePctB,
          bonusYearData?.persistencyBonusLevelFourPctB,
        ]),
        label: "Persistency Payouts (B)",
      },
    };
  }

  return persistencyPayouts;
};

type UseBrokerSummaryProps<T extends CarrierName> = {
  carrierName: T;
  carrierRuleId?: string;
  startOfYearPremiumInput?: number;
  bonusYear?: number;
};

type UseBrokerSummaryResults = {
  newBusinessPayouts?: string;
  newBusinessThresholds: string | null;
  persistencyPayouts:
    | string
    | {
        persistencyPayoutsA: PersistencyPayout;
        persistencyPayoutsB: PersistencyPayout;
      }
    | null;
  loading: boolean;
  bonusYearSettingsData: CarrierBonusYear | null;
};

export function useBrokerSummary<T extends CarrierName>({
  carrierName,
  carrierRuleId,
  startOfYearPremiumInput,
  bonusYear,
}: UseBrokerSummaryProps<T>): UseBrokerSummaryResults {
  const carrierType = CARRIER_NAME_MAP[carrierName];

  const { data, isLoading } = useCurrentBonusYearSettingsQuery<
    CarrierKeyForName<T>
  >({ carrierType, currentRuleId: carrierRuleId, bonusYear });

  const newBusinessPayouts = isPrincipalSettings(data, carrierType)
    ? data?.nbPayouts
    : isLincolnSettings(data, carrierType)
      ? formatPayouts(getLincolnNbLevels(data).map((level) => level.payout))
      : formatPayouts([
          (data as GuardianBonusYearSettings)?.newBonusLevelOnePct,
          (data as GuardianBonusYearSettings)?.newBonusLevelTwoPct,
          (data as GuardianBonusYearSettings)?.newBonusLevelThreePct,
        ]);
  const newBusinessThresholds = data
    ? getBusinessThresholds(carrierType, data, startOfYearPremiumInput)
    : undefined;
  const persistencyPayouts = getPersistencyPayouts(carrierType, data);

  return {
    newBusinessPayouts,
    newBusinessThresholds: newBusinessThresholds || null,
    persistencyPayouts,
    loading: isLoading,
    bonusYearSettingsData: data || null,
  };
}
